import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Hook que devuelve la "página actual" de un array y los controles para cambiarla.
 * Uso:
 *   const { paged, pagination } = usePagedRows(rows, { initialPageSize: 25 });
 *   ...{paged.map(...)} ... <PaginationBar {...pagination} />
 */
export function usePagedRows(rows, { initialPageSize = 25 } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const total = rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    if (!rows) return [];
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  // Reset page if rows shrink
  React.useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  return {
    paged,
    pagination: {
      page: safePage,
      pageSize,
      total,
      totalPages,
      setPage,
      setPageSize: (sz) => { setPageSize(sz); setPage(1); },
    },
  };
}

export default function PaginationBar({ page, pageSize, total, totalPages, setPage, setPageSize, sizeOptions = [10, 25, 50, 100] }) {
  if (total <= sizeOptions[0]) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2 px-1 text-xs text-gray-600">
      <div data-testid="pagination-info">
        Mostrando <strong>{start}</strong>–<strong>{end}</strong> de <strong>{total}</strong>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Filas por página</span>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10))}>
          <SelectTrigger className="h-8 w-20 text-xs" data-testid="pagination-page-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sizeOptions.map((s) => (
              <SelectItem key={s} value={String(s)}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-1">
          <strong>{page}</strong> / {totalPages}
        </span>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="pagination-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
